import { useTranslation } from 'react-i18next';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import message from 'antd/es/message';

import {
  useCurrentAddressInfo,
  useUpdateCurrentAddressInfo,
  useCurrentAddressAccount,
  useCurrentL2KeyList,
} from '@/hooks';
import { encrypt, generateKey } from '@/utils';

import { PasswordInput } from '../PasswordInput';
import { AccountModal } from './AccountModal';
import { SuccessHeader } from '../SuccessHeader';
import style from './index.module.css';

const useRetrieveAccount = (
  onClose: () => void,
  isForgetPassword: boolean,
  isCreateKey?: boolean,
) => {
  const { t } = useTranslation();

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showHeaderTips, setShowHeaderTips] = useState(isForgetPassword);
  const { address } = useCurrentAddressInfo();
  const [buttonText, setButtonText] = useState(t('AccountModal_AccountRetrieveModal.send-request'));
  const [privateKey, setPrivateKey] = useState('');
  const [publicKey, setPublicKey] = useState('');

  const { data: accountData, ...accountQuery } = useCurrentAddressAccount();
  const { data: l2KeyListData, ...l2KeyListDataQuery } = useCurrentL2KeyList();
  const { account } = useCurrentAddressInfo();
  useEffect(() => {
    setIsButtonDisabled(!accountQuery.isFetched || !l2KeyListDataQuery.isFetched);
  }, [accountQuery.isFetched, l2KeyListDataQuery.isFetched]);

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const putAccountInfo = useUpdateCurrentAddressInfo();

  const onPasswordChange = useCallback(() => {
    setIsButtonDisabled(passwordInputRef?.current?.value?.length !== 6);
  }, []);

  const steps = [
    {
      name: t('AccountModal_AccountRetrieveModal.step1Title'),
      handler: async (next: () => void) => {
        if (!address || !l2KeyListData?.primaryL2Key) {
          onClose();
          return;
        }

        try {
          const nonce = l2KeyListData?.primaryL2Key?.nonce || 1;
          const { privateKey, publicKey } = await generateKey(
            address,
            nonce,
            l2KeyListData?.primaryL2Key?.chain_id,
          );

          if (
            account?.publicKey && // make sure user can access the setting menu
            l2KeyListData?.primaryL2Key.public_key &&
            publicKey &&
            l2KeyListData?.primaryL2Key.public_key !== publicKey
          ) {
            message.error(t('AccountModal_AccountRetrieveModal.keyWrong'));
            onClose();
            return;
          }

          setPrivateKey(privateKey);
          setPublicKey(publicKey);
          setIsButtonDisabled(true);
          setButtonText(t('AccountModal_AccountRetrieveModal.confirm'));
          setShowHeaderTips(false);
          next();
        } catch (e) {
          Sentry.captureException(e);
        }
      },
      textWidth: 64,
      content: (
        <div className={style.headroomBody}>
          <div className={style.subTitle}>
            {isCreateKey ? t('AccountInfo.1jl4p5n') : t('AccountModal_AccountRetrieveModal.i7rf1a')}
          </div>
          <div className={style.description}>{t('AccountModal_AccountRetrieveModal.15atmuz')}</div>
        </div>
      ),
    },
    {
      name: t('AccountModal_AccountRetrieveModal.step2Title'),
      handler: (next: () => void) => {
        const password = passwordInputRef?.current?.value;
        if (!password || password.length !== 6) {
          return;
        }
        try {
          const encryptedPrivateKey = encrypt(privateKey, password);

          if (address) {
            setIsLoading(true);
            putAccountInfo({
              account: {
                publicKey,
                encryptedPrivateKey,
                chainId: l2KeyListData?.primaryL2Key?.chain_id,
              },
            });

            // make sure that the account info is saved to local storage
            setTimeout(() => {
              setIsLoading(false);
              onClose();
            }, 1500);
          }
        } catch (e) {
          Sentry.captureException(e);
        }
      },
      textWidth: 114,
      content: (
        <div className={style.headroomBody}>
          <SuccessHeader
            text={
              isCreateKey
                ? t('AccountModal_AccountRetrieveModal.createdSus')
                : t('AccountModal_AccountRetrieveModal.retrieved-successfully')
            }></SuccessHeader>
          <div className={style.passwordTitle}>{t('AccountModal_AccountRetrieveModal.e7y8hm')}</div>
          <PasswordInput
            className={style.registerPassword}
            passwordInputRef={passwordInputRef}
            onPasswordChange={onPasswordChange}
            showInputTips
          />
        </div>
      ),
    },
  ];

  return {
    steps,
    isLoading,
    buttonText,
    isButtonDisabled,
    showHeaderTips,
  };
};

export const AccountRetrieveModal = ({
  turnOff,
  isForgetPassword,
  isCreateKey,
}: {
  turnOff(): void;
  isForgetPassword: boolean;
  isCreateKey?: boolean;
}) => {
  const { t } = useTranslation();

  const { steps, isLoading, buttonText, isButtonDisabled, showHeaderTips } = useRetrieveAccount(
    turnOff,
    isForgetPassword,
    isCreateKey,
  );

  return (
    <AccountModal
      headerTitle={
        isCreateKey
          ? t('AccountInfo.1jl4p5n')
          : isForgetPassword
            ? t('AccountModal_AccountRetrieveModal.forgot-password')
            : t('AccountModal_AccountRetrieveModal.retrieve-key')
      }
      turnOff={turnOff}
      steps={steps}
      isLoading={isLoading}
      buttonText={buttonText}
      isButtonDisabled={isButtonDisabled}
      headerTips={
        showHeaderTips && isForgetPassword ? t('AccountModal_AccountRetrieveModal.retrieveTip') : ''
      }
    />
  );
};
